import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { 
  PieChart, Plus, Trash2, Loader2, AlertTriangle, CheckCircle
} from 'lucide-react';
import ExportButton from './ExportButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BudgetManagement = ({ token }) => {
  const [budgets, setBudgets] = useState([]);
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [newBudget, setNewBudget] = useState({
    year: new Date().getFullYear(),
    month: null,
    category: '',
    amount: ''
  });

  useEffect(() => {
    fetchBudgets();
    fetchComparison();
  }, [selectedYear]);

  const fetchBudgets = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/budgets?year=${selectedYear}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBudgets(response.data);
    } catch (error) {
      console.error('Error fetching budgets:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchComparison = async () => {
    try {
      const response = await axios.get(`${API}/admin/budgets/comparison?year=${selectedYear}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setComparison(response.data);
    } catch (error) {
      console.error('Error fetching comparison:', error);
    }
  };

  const handleAddBudget = async () => {
    try {
      await axios.post(`${API}/admin/budgets`, {
        ...newBudget,
        month: newBudget.month ? parseInt(newBudget.month) : null,
        category: newBudget.category || null,
        amount: parseFloat(newBudget.amount)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Budget ajouté');
      setShowAddDialog(false);
      setNewBudget({
        year: selectedYear,
        month: null,
        category: '',
        amount: ''
      });
      fetchBudgets();
      fetchComparison();
    } catch (error) {
      toast.error('Erreur lors de l\'ajout');
    }
  };

  const handleDeleteBudget = async (id) => {
    if (!confirm('Supprimer ce budget ?')) return;
    try {
      await axios.delete(`${API}/admin/budgets/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Budget supprimé');
      fetchBudgets();
      fetchComparison();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('fr-CA', { style: 'currency', currency: 'CAD' }).format(value);
  };

  return (
    <div className="space-y-6">
      {/* Year Selector & Add Button */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-col sm:flex-row gap-4 justify-between items-center">
            <div className="flex items-center gap-2">
              <Label>Année:</Label>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                className="px-3 py-2 border rounded-md"
              >
                {[2024, 2025, 2026, 2027, 2028].map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <ExportButton token={token} exportType="budgets" year={selectedYear} />
              <Button onClick={() => setShowAddDialog(true)} className="bg-brand-600">
                <Plus className="h-4 w-4 mr-2" />
                Définir un budget
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Budget vs Actual Comparison */}
      {comparison && comparison.comparison.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PieChart className="h-5 w-5 text-brand-600" />
              Budget vs Réel - {selectedYear}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {comparison.comparison.map((item, index) => (
                <div key={index} className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="font-medium">{item.category}</span>
                    <div className="flex items-center gap-2">
                      <span className={item.status === 'over' ? 'text-red-600' : 'text-green-600'}>
                        {formatCurrency(item.actual)} / {formatCurrency(item.budget)}
                      </span>
                      {item.status === 'over' ? (
                        <AlertTriangle className="h-4 w-4 text-red-500" />
                      ) : (
                        <CheckCircle className="h-4 w-4 text-green-500" />
                      )}
                    </div>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div
                      className={`h-3 rounded-full transition-all ${
                        item.percentage > 100 ? 'bg-red-500' : item.percentage > 80 ? 'bg-yellow-500' : 'bg-green-500'
                      }`}
                      style={{ width: `${Math.min(item.percentage, 100)}%` }}
                    />
                  </div>
                  <p className="text-xs text-gray-500">
                    {item.percentage}% utilisé • {item.status === 'over' ? 'Dépassement: ' : 'Restant: '}
                    {formatCurrency(Math.abs(item.difference))}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Budgets List */}
      <Card>
        <CardHeader>
          <CardTitle>Budgets définis</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-brand-600" />
            </div>
          ) : budgets.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4 font-semibold text-gray-600">Type</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-600">Catégorie</th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-600">Montant</th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {budgets.map((budget) => (
                    <tr key={budget.id} className="border-b hover:bg-gray-50">
                      <td className="py-3 px-4">
                        {budget.month ? `Mensuel (${budget.month}/${budget.year})` : `Annuel ${budget.year}`}
                      </td>
                      <td className="py-3 px-4">{budget.category || 'Global'}</td>
                      <td className="py-3 px-4 text-right font-semibold">{formatCurrency(budget.amount)}</td>
                      <td className="py-3 px-4 text-right">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeleteBudget(budget.id)}
                          className="text-red-600"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500">
              Aucun budget défini pour {selectedYear}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Budget Dialog */}
      <AlertDialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Définir un budget</AlertDialogTitle>
          </AlertDialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Année</Label>
                <select
                  value={newBudget.year}
                  onChange={(e) => setNewBudget({...newBudget, year: parseInt(e.target.value)})}
                  className="w-full px-3 py-2 border rounded-md"
                >
                  {[2024, 2025, 2026, 2027, 2028].map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label>Mois (optionnel)</Label>
                <select
                  value={newBudget.month || ''}
                  onChange={(e) => setNewBudget({...newBudget, month: e.target.value})}
                  className="w-full px-3 py-2 border rounded-md"
                >
                  <option value="">Annuel</option>
                  {Array.from({length: 12}, (_, i) => i + 1).map(m => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Catégorie (optionnel)</Label>
              <Input
                value={newBudget.category}
                onChange={(e) => setNewBudget({...newBudget, category: e.target.value})}
                placeholder="Laisser vide pour budget global"
              />
            </div>
            <div className="space-y-2">
              <Label>Montant</Label>
              <Input
                type="number"
                step="0.01"
                value={newBudget.amount}
                onChange={(e) => setNewBudget({...newBudget, amount: e.target.value})}
                placeholder="0.00"
              />
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleAddBudget} className="bg-brand-600">
              Définir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default BudgetManagement;
